import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./WhoItsFor.module.scss";

const WhoItsFor: FC = () => {
  return (
    <div className={styles.whoItsForPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Who It's For"
        heading="Building information for the people who need it."
        copy="Owners, property teams, facilities professionals, building operators and O&M specialists all need access to building information. Pulse provides a shared digital environment where the information behind a building can be organised, found and used."
        backgroundImage={require("../../assets/images/who-its-for/who-its-for-hero.webp")}
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Common Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Diverse Needs</span>
            <h2 className={styles.sectionHeading}>
              Different people need the same building information for different
              reasons.
            </h2>
            <p className={styles.sectionCopy}>
              A building contains a huge amount of information, but the people
              using it don't all need the same things. A building owner may need
              to understand the long-term information record. A facilities team
              needs maintenance and technical information. An engineer needs a
              drawing or equipment manual while standing in a plant room.
            </p>
            <p className={styles.sectionCopy}>
              The underlying information may be the same. The way people use it
              is different.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-shared-information.webp")}
              alt="Shared building information across different user types"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. Who Uses Pulse? */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Audiences</span>
          <h2 className={styles.sectionHeading}>
            One platform. Different needs.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse serves everyone involved in the operation, management and
            technical support of commercial buildings.
          </p>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Building Owners</h3>
            <p>
              Maintain a long-term digital record of the building and its
              information.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Property & Facilities Teams</h3>
            <p>
              Find the information needed to manage and operate the building
              day-to-day.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Building Operators & Engineers</h3>
            <p>
              Access technical information and drawings when working on site.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>O&M Companies</h3>
            <p>
              Provide clients with a modern digital platform for the building
              information they create.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Project & Handover Teams</h3>
            <p>
              Help transfer building information into a structured digital
              environment at handover.
            </p>
          </div>
        </div>
      </Section>

      {/* 4. Building Owners */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Owners</span>
            <h2 className={styles.sectionHeading}>
              Keep the information about your building accessible for the long
              term.
            </h2>
            <p className={styles.sectionCopy}>
              Building information remains important for years after a project
              is completed. Owners need access to O&M records, as-built
              drawings, asset data, warranties and certificates over the life of
              the building.
            </p>
            <p className={styles.sectionCopy}>
              Pulse provides a structured digital environment to preserve
              institutional knowledge and support future changes without relying
              on individual contractors.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-building-owners.webp")}
              alt="Building owner reviewing property records"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 5. Property & Facilities Teams */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-facilities-team.webp")}
              alt="Facilities manager using Pulse"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Property & Facilities</span>
            <h2 className={styles.sectionHeading}>
              Get to the information needed to manage the building.
            </h2>
            <p className={styles.sectionCopy}>
              Property and facilities teams sit at the centre of a building's
              operation. They need to find equipment information, certificates,
              maintenance procedures and drawings quickly.
            </p>
            <p className={styles.sectionCopy}>
              Pulse gives teams a structured place to access that information
              without searching through scattered folders or relying on memory.
            </p>
          </div>
        </div>
      </Section>

      {/* 6. Building Operators & Engineers */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Operators & Engineers</span>
            <h2 className={styles.sectionHeading}>
              Put technical information where the work happens.
            </h2>
            <p className={styles.sectionCopy}>
              Engineers and building operators often need equipment manuals,
              asset records and drawings while working away from their desks in
              plant rooms and service areas.
            </p>
            <p className={styles.sectionCopy}>
              Pulse makes the same building information accessible from mobile
              devices and tablets, supported by QR code scanning on physical
              equipment.
            </p>
            <div className={styles.actionLink}>
              <Link to="/documents-drawings" className={styles.textLink}>
                Explore Documents & Drawings →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/mobile/engineer-using-pulse.webp")}
              alt="Engineer using Pulse on mobile in plant room"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 7. O&M Companies */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-om-companies.webp")}
              alt="O&M professional compiling technical records"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>O&M Companies</span>
            <h2 className={styles.sectionHeading}>
              You create the building information. Pulse makes it easier to use.
            </h2>
            <p className={styles.sectionCopy}>
              O&M companies have specialist expertise in collecting, compiling
              and organising building information. Pulse provides a digital
              platform for that information once created.
            </p>
            <p className={styles.sectionCopy}>
              Instead of delivering documentation that disappears into static
              PDFs, O&M providers can offer clients a structured digital
              environment with search, mobile access and AI capabilities.
            </p>
            <div className={styles.actionLink}>
              <Link to="/digital-om" className={styles.textLink}>
                Explore Digital O&M →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 8. Project & Handover Teams */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Project & Handover</span>
            <h2 className={styles.sectionHeading}>
              Give building information somewhere useful to go after handover.
            </h2>
            <p className={styles.sectionCopy}>
              Construction and fit-out projects produce a large volume of
              information including as-built drawings, O&M manuals, asset data
              and certificates.
            </p>
            <p className={styles.sectionCopy}>
              Pulse provides the digital environment into which this handover
              information can be structured and made accessible for ongoing
              operational use.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-handover.webp")}
              alt="Project handover documentation review"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 9. How Everyone Benefits */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits Summary</span>
          <h2 className={styles.sectionHeading}>
            One building record. Different benefits.
          </h2>
        </div>
        <table className={styles.comparisonTable}>
          <thead>
            <tr>
              <th>Audience</th>
              <th>What they need</th>
              <th>How Pulse helps</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Building Owners</td>
              <td>Long-term building information & continuity</td>
              <td>Central digital building record</td>
            </tr>
            <tr>
              <td>Property & Facilities Teams</td>
              <td>Operational and technical information access</td>
              <td>Structured access, search & metadata</td>
            </tr>
            <tr>
              <td>Operators & Engineers</td>
              <td>Information while working on site</td>
              <td>Mobile and QR-based on-site access</td>
            </tr>
            <tr>
              <td>O&M Companies</td>
              <td>A better way to deliver client information</td>
              <td>Digital platform for compiled records</td>
            </tr>
          </tbody>
        </table>
      </Section>

      {/* 10. One Building Record */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Shared Platform</span>
            <h2 className={styles.sectionHeading}>
              Everyone works from the same building information.
            </h2>
            <p className={styles.sectionCopy}>
              Different teams interact with the building at different times.
              Pulse provides a shared digital environment rather than creating
              separate information silos for each audience.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-building-record.webp")}
              alt="Central building record supporting multiple teams"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 11. Different Users, Different Needs */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Perspectives</span>
          <h2 className={styles.sectionHeading}>
            The same information can answer different questions.
          </h2>
        </div>
        <div className={styles.quotesGrid}>
          <div className={styles.quoteCard}>
            <h4>Building Owner</h4>
            <blockquote>
              "What information do we have for this building across its
              lifecycle?"
            </blockquote>
          </div>
          <div className={styles.quoteCard}>
            <h4>Facilities Manager</h4>
            <blockquote>
              "Where is the O&M manual and maintenance schedule for AHU-003?"
            </blockquote>
          </div>
          <div className={styles.quoteCard}>
            <h4>Engineer</h4>
            <blockquote>
              "Which drawing shows the chilled water connection in the plant
              room?"
            </blockquote>
          </div>
          <div className={styles.quoteCard}>
            <h4>O&M Professional</h4>
            <blockquote>
              "Has all the necessary asset and drawing information been
              provided?"
            </blockquote>
          </div>
        </div>
        <div
          className={styles.splitImageContainer}
          style={{ marginTop: "3rem" }}
        >
          <img
            src={require("../../assets/images/who-its-for/who-its-for-different-questions.webp")}
            alt="Different user questions surrounding central building information"
            className={styles.productScreenshot}
            loading="lazy"
          />
        </div>
      </Section>

      {/* 12. Example: A Building in Use */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-building-in-use.webp")}
              alt="Commercial building in daily operation"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Operational Workflow</span>
            <h2 className={styles.sectionHeading}>
              One building. Many people. One information platform.
            </h2>
            <p className={styles.sectionCopy}>
              From morning facilities checks and plant-room servicing by
              engineers to project updates by O&M teams and long-term asset
              management by owners, Pulse provides a common information
              environment throughout.
            </p>
          </div>
        </div>
      </Section>

      {/* 13. Pulse Across the Building Lifecycle */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Lifecycle Continuity</span>
            <h2 className={styles.sectionHeading}>
              Useful from handover through the life of the building.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse sits across the building information lifecycle—from design
              and construction through handover, operation, maintenance and
              ongoing management—keeping data accessible.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/who-its-for-lifecycle.webp")}
              alt="Building information lifecycle diagram"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 14. Why Pulse Works Across Teams */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Platform Role</span>
          <h2 className={styles.sectionHeading}>
            One platform, without forcing everyone into the same workflow.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse provides the shared information layer. It sits alongside CAFM,
            CMMS, property management and enterprise systems as the definitive
            building information foundation.
          </p>
        </div>
      </Section>

      {/* 15. Demo */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>See Pulse</span>
            <h2 className={styles.sectionHeading}>
              See how Pulse works for your team.
            </h2>
            <p className={styles.sectionCopy}>
              Explore a working example of Pulse and see how building
              information, documents, drawings, assets, search and AI work
              together across different stakeholders.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/who-its-for/overview.webp")}
              alt="Pulse platform overview showcase"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 16. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Give everyone a better way to access your building information."
        copy="Whether you're responsible for the building, managing its day-to-day operation, working with its technical information or creating its O&M documentation, Pulse provides a shared digital environment."
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(WhoItsFor)({
  meta: globalContent.seo.whoItsFor,
});
